import React from 'react'
import {
  TableHead as MuiTableHead,
  Table as MuiTable,
  TableRow,
  TableCell,
  TableSortLabel,
  TableBody,
  Paper,
  TableContainer,
  PaperProps,
  Tooltip,
  Box,
} from '@mui/material'
import { J1DownarrowA, K1Information } from '@e3dc-react/icons'
import { useCustomTranslation } from '../../../Hooks/useCustomTranslation'
import { CurrentSort, TableProps } from '../types'
import useStyles from '../styles'
import SpacerRow from './SpaceRow'
import CustomTableRow from './CustomTableRow'

const PaperTableHead: React.FC<PaperProps<'thead'>> = props => <Paper component={'thead'} {...props} />
const PaperTableBody: React.FC<PaperProps<'tbody'>> = props => <Paper component={'tbody'} {...props} />
const TooltipElement: React.FC<{ tooltip: string }> = ({ tooltip }) => (
  <Tooltip title={tooltip} placement="bottom-start">
    <i className="flex align-center">
      <K1Information />
    </i>
  </Tooltip>
)

const Table = <T extends { [key: string]: unknown }>({
  data,
  header,
  onSelect,
  currentSort,
  setCurrentSort,
  hasColoredTableColumns = false,
  customFunctionalities,
  headerElevation = 1,
  bodyElevation = 1,
  collapsibleContent,
  collapsibleContentPadding,
  headerClassName,
  containerClassName,
  muiTableClassName,
  customFunctionalityClass,
  bodyClassName,
  nonSortableColumns = [],
  noData,
}: TableProps<T>): React.ReactElement => {
  const { t } = useCustomTranslation()
  const { classes } = useStyles({})
  const sortable = currentSort && setCurrentSort

  const isSortingActive = (name: string): boolean => {
    if (Array.isArray(currentSort)) {
      return !!currentSort.find(sort => sort.name === name)
    } else {
      return currentSort?.name === name
    }
  }

  const getSortByName = (name: string): CurrentSort<T> | undefined => {
    if (Array.isArray(currentSort)) {
      return currentSort.find(sort => sort.name === name)
    } else {
      return currentSort?.name === name ? currentSort : undefined
    }
  }

  const isTableHeaderSortable = (key: string, label: string): boolean => {
    return !!key && !!label && !!sortable && !nonSortableColumns.includes(label)
  }

  return (
    <TableContainer className={containerClassName}>
      <MuiTable key={'body-table'} className={muiTableClassName}>
        <MuiTableHead component={PaperTableHead} elevation={headerElevation}>
          <TableRow className={headerClassName}>
            {collapsibleContent && <TableCell key={'collapse-icon-header-cell'} padding={collapsibleContentPadding}></TableCell>}
            {header.map(({ label, key, tooltip }, index) => {
              return (
                <TableCell
                  key={`cell-${index}`}
                  onClick={() => {
                    if (sortable && !nonSortableColumns.includes(label)) {
                      const sort = getSortByName(key)
                      if (sort) {
                        setCurrentSort({ name: sort.name, direction: sort.direction === 'asc' ? 'desc' : 'asc' })
                      } else {
                        setCurrentSort({ name: key, direction: 'asc' })
                      }
                    }
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                    }}
                  >
                    {isTableHeaderSortable(key, label) ? (
                      <TableSortLabel active={isSortingActive(key)} direction={getSortByName(key)?.direction ?? 'asc'} IconComponent={J1DownarrowA}>
                        {label}
                      </TableSortLabel>
                    ) : (
                      label ?? ''
                    )}
                    {tooltip && <TooltipElement tooltip={tooltip} />}
                  </Box>
                </TableCell>
              )
            })}
            {customFunctionalities?.map((name, index) => (
              <TableCell key={`customFunction-${index}`} />
            ))}
          </TableRow>
        </MuiTableHead>
        <TableBody component={PaperTableBody} elevation={bodyElevation} className={classes.spacer + 'dedo-mi-prucko'}>
          <SpacerRow cellCount={data.length} />
          {data.length === 0 ? (
            <TableRow className={classes.lastRow}>
              <TableCell align="center" colSpan={header.length + (customFunctionalities?.length ?? 0)}>
                {noData ?? t('shell.noData')}
              </TableCell>
            </TableRow>
          ) : (
            data.map((row, index) => {
              return (
                <CustomTableRow
                  key={`row-${index}`}
                  rowData={row}
                  rowIndex={index}
                  onSelect={onSelect ? () => onSelect(index) : undefined}
                  customFunctionalities={customFunctionalities}
                  collapsibleContent={collapsibleContent ? () => collapsibleContent(index) : undefined}
                  columnCount={header.length + (customFunctionalities?.length ?? 0)}
                  hasColoredTableColumns={hasColoredTableColumns}
                  customFunctionalityClass={customFunctionalityClass}
                  bodyClassName={bodyClassName}
                />
              )
            })
          )}
        </TableBody>
      </MuiTable>
    </TableContainer>
  )
}

export default Table
